import React from 'react';
import {
  Button,
  Grid,
  MenuItem,
  Typography,
  ButtonGroup,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { InputTextNullable } from 'components/input/components';
import { Form } from '../../../form';
import { MaskedSwedishPostNumberInput } from '../../../input/components/masked-swedish-postnumber-input';
import { FormSectionInfo } from '../../../form/components';
import { CheckboxLabelled } from '../../../../packages/checkbox-labelled';
import {
  RHFInputText,
  RHFAutocomplete,
} from '../../../../packages/react-hook-form';
import { InputText } from '../../../input';
import { RHFSelect } from '../../../../packages/react-hook-form/select';
import { serviceFeeUnavailableMessage } from '../../buy-process-form-utils';
import { FormSectionContainer } from '../../../form/components/form-section-container/form-section-container';
import { BuyProcessStepsTestIds } from '../../../../utils/testing/buy-process-steps-test-ids';
import { BuyProcessFormNextButton } from '../buy-process-form-next-button/buy-process-form-next-button';
import { BuyProcessFormAdminSubmitButton } from '../buy-process-form-admin-submit-button/buy-process-form-admin-submit-button';
import { useCompanyDetails } from './use-company-details';
import {
  businessTemplates,
  businessTemplatesDefault,
} from './company-details-utils';
import {
  CompanyDetailsFieldLabel,
  CompanyDetailsFieldName,
} from './company-details-types';

const useStyles = makeStyles((theme) => ({
  formItem: {
    marginBottom: theme.spacing(1),
  },
}));

export const BuyProcessFormCompanyDetails = () => {
  const classes = useStyles();

  const {
    formMethods: { register, errors, control, setValue },
    onSubmit,
    onForward,
    fees,
    initialState,
    onBack,
    municipalities,
    isUpdating,
  } = useCompanyDetails();

  const setBusinessDescription = (
    businessTemplate: keyof typeof businessTemplates,
  ) => {
    setValue('businessDescription', businessTemplates[businessTemplate], {
      shouldValidate: true,
    });
  };

  const unavailableFeeGuard = (availableLabel: string) => {
    if (!fees) return serviceFeeUnavailableMessage;
    return availableLabel;
  };

  const nameReviewLabel = unavailableFeeGuard(
    `${CompanyDetailsFieldLabel.nameReview} (+${fees?.nameReviewFee ?? ''}kr)`,
  );
  const taxHelpLabel = unavailableFeeGuard(
    `${CompanyDetailsFieldLabel.taxHelp} (+${fees?.taxHelpFee ?? ''}kr)`,
  );

  return (
    <Form
      data-testid={BuyProcessStepsTestIds.CompanyDetails}
      onSubmit={onSubmit}
    >
      <div>
        <FormSectionInfo.InfoBoxTitled title="Ange bolagets namn">
          <Typography>
            Tänk på att välja ett namn som inte redan är taget,
            varumärkesskyddat eller för likt något annat. Namnet kan även nekas
            av Bolagsverket om det är för generellt beskrivande (t.ex.
            “Webbdesign AB”) så lägg till något mer specifikt också (t.ex.
            “Tranås Webbdesign AB”). Namnet måste innehålla ordet “aktiebolag”
            eller “AB”.
          </Typography>

          <br />

          <Typography>
            Vi vet att bolagets namn är viktigt för dig och därför rekommenderar
            vi att du fyller i några varianter så att registreringsprocessen kan
            gå snabbast möjligt om Bolagsverket nekar ditt namnval. Självklart
            gör vi vad vi kan för att ditt förstahandsval ska godkännas!
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer>
          <Grid spacing={2} container>
            <Grid item xs={12}>
              <InputText
                errors={errors}
                ref={register}
                name={CompanyDetailsFieldName.desiredCompanyName}
                label={CompanyDetailsFieldLabel.desiredCompanyName}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <InputText
                errors={errors}
                ref={register}
                name={CompanyDetailsFieldName.firstAlternativeCompanyName}
                label={CompanyDetailsFieldLabel.firstAlternativeCompanyName}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <InputText
                errors={errors}
                ref={register}
                name={CompanyDetailsFieldName.secondAlternativeCompanyName}
                label={CompanyDetailsFieldLabel.secondAlternativeCompanyName}
                required
              />
            </Grid>

            <FormSectionInfo.InfoBox style={{ marginTop: 16, marginBottom: 8 }}>
              Vi gör en granskning av era namnförslag innan ärendet skickas till
              Bolagsverket. Vid eventuella hinder hjälper vi er med nya
              namnförslag.
            </FormSectionInfo.InfoBox>

            <CheckboxLabelled
              style={{ paddingLeft: '8px' }}
              defaultChecked={initialState?.nameReview}
              ref={register}
              name={CompanyDetailsFieldName.nameReview}
              label={nameReviewLabel}
            />
          </Grid>
        </FormSectionContainer>
      </div>

      <div>
        <FormSectionInfo.InfoBoxTitled title="Bolagets adress och säte">
          <Typography>
            Fyll i det nya bolagets adressuppgifter. Säte är den kommun där
            styrelsen finns och det finns inget som hindrar att verksamheten
            bedrivs i en ort och har sitt säte i en annan.
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputTextNullable
                errors={errors}
                ref={register}
                name={CompanyDetailsFieldName.co}
                label={CompanyDetailsFieldLabel.co}
              />
            </Grid>

            <Grid item xs={12}>
              <InputText
                errors={errors}
                ref={register}
                name={CompanyDetailsFieldName.address}
                label={CompanyDetailsFieldLabel.address}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <RHFInputText
                name={CompanyDetailsFieldName.postalCode}
                defaultValue=""
                label={CompanyDetailsFieldLabel.postalCode}
                control={control}
                errors={errors}
                InputProps={{ inputComponent: MaskedSwedishPostNumberInput }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <InputText
                errors={errors}
                ref={register}
                name={CompanyDetailsFieldName.city}
                label={CompanyDetailsFieldLabel.city}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <RHFAutocomplete
                control={control}
                errors={errors}
                name={CompanyDetailsFieldName.municipality}
                label={CompanyDetailsFieldLabel.municipality}
                options={municipalities || []}
                getOptionLabel={(option) => option.value}
                getOptionSelected={(option, value) => option.id === value.id}
                groupBy={(x) => x.value.charAt(0)}
                InputProps={{ required: true }}
              />
            </Grid>
          </Grid>
        </FormSectionContainer>
      </div>

      <div>
        <FormSectionInfo.InfoBoxTitled title="Verksamhetsbeskrivning">
          <Typography>
            Beskriv verksamheten noggrant. Denna beskrivning tas med i
            Bolagsverkets bedömning vid godkännandet av ditt önskade bolagsnamn.
            Om du till exempel ska ägna dig åt konsultverksamhet så är det
            viktigt att beskriva vilken typ av konsulttjänster du säljer.
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer>
          <RHFSelect
            helperText="Utgå från ett exempel eller skriv en egen..."
            className={classes.formItem}
            name={CompanyDetailsFieldName.templateSelect}
            label={CompanyDetailsFieldLabel.templateSelect}
            control={control}
            errors={errors}
            defaultValue={
              initialState?.templateSelect || businessTemplatesDefault
            }
            SelectProps={{
              onChange: (event) => {
                setBusinessDescription(
                  event.target.value as keyof typeof businessTemplates,
                );
              },
            }}
          >
            {Object.keys(businessTemplates).map((desc, index) => (
              <MenuItem key={String(index) + desc} value={desc}>
                {desc}
              </MenuItem>
            ))}
          </RHFSelect>

          <RHFInputText
            style={{ marginTop: '16px' }}
            name={CompanyDetailsFieldName.businessDescription}
            defaultValue=""
            label={CompanyDetailsFieldLabel.businessDescription}
            control={control}
            errors={errors}
            multiline
            rows={2}
            required
          />
        </FormSectionContainer>
      </div>

      <div>
        <FormSectionInfo.InfoBoxTitled title="Ansökan om F-skatt, moms och arbetsgivare">
          <Typography>
            Vi kan hjälpa er med ansökan om F-skatt, moms och arbetsgivare.
            Kryssa i rutan nedan så får du hjälp genom processen. Snabbt, enkelt
            och smidigt!
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer>
          <Grid item xs={12}>
            <CheckboxLabelled
              defaultChecked={initialState?.taxHelp}
              ref={register}
              label={taxHelpLabel}
              name={CompanyDetailsFieldName.taxHelp}
            />
          </Grid>
        </FormSectionContainer>
      </div>

      <ButtonGroup>
        <Button onClick={onBack}>Tillbaka</Button>
        <BuyProcessFormNextButton onClick={onForward}>
          Nästa
        </BuyProcessFormNextButton>
        <BuyProcessFormAdminSubmitButton loading={isUpdating}>
          Spara
        </BuyProcessFormAdminSubmitButton>
      </ButtonGroup>
    </Form>
  );
};
